/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";

import {EmGroup} from '../../models/em-group';
import {GroupType} from '../../models/group-type';
import {InstanceData} from "../../models/instance-data";
import {ServerObject} from "../../models/server-object";
import {ServerObjectInstance} from "../../models/server-object-instance";

import {GroupService} from '../../services/group.service';
import {ServerObjectService} from "../../services/server-object.service";

import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE} from "../../app-constants";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { FilterOption, FilterType, REMOVE_FILTER } from '../../models/filter-option';



@Component({
  templateUrl: './server-objects.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class ServerObjectsComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mServerObjects: ServerObjectUI[] = [];
  mServerObjectInstances: ServerObjectInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Server Objects, 2 = Server Object Instances


  //Entity Type Dialog Variables
  mEntity: ServerObject;
  mEntityName: string;
  mEntityType = GroupType.SERVEROBJECT;
  mEntityTypeName = "Server Object";

  // All the Filter Options available
  filterOptionForServerObjects: FilterOption[] = [];
  filterOptionForServerObjectInstances: FilterOption[] = [];

  // The Filter Option Selected by the user
  selectedFilterOptionForServerObject: FilterOption = REMOVE_FILTER;
  selectedFilterOptionForServerObjectInstance: FilterOption = REMOVE_FILTER;
  /** Active filters for the Server Object Instances (Name + Type), AND'd together */
  activeFilterOptionsForServerObjectInstance: FilterOption[] = [];

  //Entity Instance Dialog Variables
  mInstance: ServerObjectInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: ServerObject;
  mInstanceType = GroupType.SERVEROBJECTINSTANCE;
  mInstanceTypeName = "Server Object";
  mAvailableEntityTypes: ServerObject[];

  // // paginator variables
  currentPageForServerObject = 1;
  currentPageForServerObjectInstance = 1;

  totalNumberOfServerObjects = 0;
  totalNumberOfServerObjectInstances = 0;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  constructor(
    private mRoute: ActivatedRoute,
    private mServerObjectService: ServerObjectService,
    private mGroupService: GroupService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) {}


  ngOnInit(): void {
    this.refreshFilterOptionsForServerObjects();
    this.refreshFilterOptionsForServerObjectInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];
    this.mRoute.data.subscribe((data: 
      { 
        serverObjects: PagedResults<ServerObject>, 
        serverObjectInstances: PagedResults<ServerObjectInstance>}
      ) => {
        this.updatePagedServerObject(data.serverObjects);
        this.updatePagedServerObjectInstance(data.serverObjectInstances)
    });
  }

 

  isSingleServerObjectSelected(): boolean {
    if (this.mServerObjects != null && this.mServerObjects.length > 0) {
      let oneSelected = false;
      for (const serverObject of this.mServerObjects) {
        if (serverObject.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyServerObjectsSelected(): boolean {
    return this.mServerObjects.some(serverObject => serverObject.mSelected);
  }

  isAllServerObjectsSelected(): boolean {
    return this.mServerObjects !== null && this.mServerObjects.every(serverObject => serverObject.mSelected);
  }

  setAllServerObjectsSelected(event: any): void {
    this.mServerObjects.forEach(serverObject => serverObject.mSelected = event.target.checked);
  }





  isSingleServerObjectInstanceSelected(): boolean {
    if (this.mServerObjectInstances != null && this.mServerObjectInstances.length > 0) {
      let oneSelected = false;
      for (const serverObjectInstance of this.mServerObjectInstances) {
        if (serverObjectInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyServerObjectInstancesSelected(): boolean {
    return this.mServerObjectInstances.some(serverObjectInstance => serverObjectInstance.mSelected);
  }

  isAllServerObjectInstancesSelected(): boolean {
    return this.mServerObjectInstances !== null && this.mServerObjectInstances.every(serverObjectInstance => serverObjectInstance.mSelected);
  }

  setAllServerObjectInstancesSelected(event: any): void {
    this.mServerObjectInstances.forEach(serverObjectInstance => serverObjectInstance.mSelected = event.target.checked);
  }





  getSelectedServerObjects(): ServerObject[] {
    const serverObjects: ServerObject[] = [];
    this.mServerObjects.filter(ui => ui.mSelected).forEach(ui => serverObjects.push(ui.mServerObject));
    return serverObjects;
  }

  getSelectedServerObjectIds(): number[] {
    const ids: number[] = [];
    this.mServerObjects.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mServerObject.id));
    return ids;
  }

  getSelectedServerObjectNames(): string[] {
    const names: string[] = [];
    this.mServerObjects.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mServerObject.name));
    return names;
  }





  getSelectedServerObjectInstances(): ServerObjectInstance[] {
    const serverObjectInstances: ServerObjectInstance[] = [];
    this.mServerObjectInstances.filter(ui => ui.mSelected).forEach(ui => serverObjectInstances.push(ui.mServerObjectInstance));
    return serverObjectInstances;
  }

  getSelectedServerObjectInstanceIds(): number[] {
    const ids: number[] = [];
    this.mServerObjectInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mServerObjectInstance.id));
    return ids;
  }

  getSelectedServerObjectInstanceNames(): string[] {
    const names: string[] = [];
    this.mServerObjectInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mServerObjectInstance.name));
    return names;
  }





  manageServerObjectGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.SERVEROBJECT);
  }

  manageServerObjectInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.SERVEROBJECTINSTANCE);
  }





  addSelectedServerObjectsToGroupClicked(): void {
    if (this.isAnyServerObjectsSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.SERVEROBJECT, this.getSelectedServerObjectNames());
    }
  }

  addSelectedServerObjectInstancesToGroupClicked(): void {
    if (this.isAnyServerObjectInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.SERVEROBJECTINSTANCE, this.getSelectedServerObjectInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.SERVEROBJECT: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedServerObjectIds())
          .then(
            success => {
              this.refreshServerObjects();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.SERVEROBJECTINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedServerObjectInstanceIds())
          .then(
            success => {
              this.refreshServerObjectInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  removeSelectedServerObjectsFromGroupClicked(): void {
    if (this.isAnyServerObjectsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const serverObjectNames: string[] = [];
      this.getSelectedServerObjects().forEach(serverObject => {
        serverObjectNames.push(serverObject.name);
        if (serverObject.groups != null) {
          serverObject.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, serverObjectNames);
    }
  }

  removeSelectedServerObjectInstancesFromGroupClicked(): void {
    if (this.isAnyServerObjectInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const serverObjectInstanceNames: string[] = [];
      this.getSelectedServerObjectInstances().forEach(serverObjectInstance => {
        serverObjectInstanceNames.push(serverObjectInstance.name);
        if (serverObjectInstance.groups != null) {
          serverObjectInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, serverObjectInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.SERVEROBJECT: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedServerObjectIds())
          .then(
            success => {
              this.refreshServerObjects();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.SERVEROBJECTINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedServerObjectInstanceIds())
          .then(
            success => {
              this.refreshServerObjectInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.SERVEROBJECT: {
        this
          .mGroupService
          .getAllServerObjectsInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.SERVEROBJECTINSTANCE: {
        this
          .mGroupService
          .getAllServerObjectInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  addServerObjectClicked(): void {
    this.mEntity = new ServerObject();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedServerObjectClicked(): void {
    if (this.isSingleServerObjectSelected()) {
      this.editServerObject(this.getSelectedServerObjects()[0]);
    }
  }

  duplicateSelectedServerObjectClicked(): void {
    if (this.isSingleServerObjectSelected()) {
      this
        .mServerObjectService
        .getServerObjectById(this.getSelectedServerObjects()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedServerObjectsClicked(): void {
    if (this.isAnyServerObjectsSelected()) {
      this.mDeleteFlag = 1; //1 = Server Objects
      this.mConfirmDeleteDialog.show("server object type", this.getSelectedServerObjectNames());
    }
  }

  editServerObject(serverObject: ServerObject): void {
    this
      .mServerObjectService
      .getServerObjectById(serverObject.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
              this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Server Object... Create it
      this
        .mServerObjectService
        .createServerObject(this.mEntity)
        .then(
          response => {
            this.refreshServerObjects();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Server Object... Update it
      this
        .mServerObjectService
        .updateServerObject(this.mEntity)
        .then(
          response => {
            this.refreshServerObjects();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }





  addServerObjectInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ServerObjectInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataAndShowDialog();
  }

  editSelectedServerObjectInstanceClicked(): void {
    if (this.isSingleServerObjectInstanceSelected()) {
      this.editServerObjectInstance(this.getSelectedServerObjectInstances()[0]);
    }
  }

  duplicateSelectedServerObjectInstanceClicked(): void {
    if (this.isSingleServerObjectInstanceSelected()) {
      this
      .mServerObjectService
      .getServerObjectInstanceById(this.getSelectedServerObjectInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedServerObjectInstancesClicked(): void {
    if (this.isAnyServerObjectInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Server Object Instances
      this.mConfirmDeleteDialog.show("server object", this.getSelectedServerObjectInstanceNames());
    }
  }

  editServerObjectInstance(serverObjectInstance: ServerObjectInstance): void {
    this
      .mServerObjectService
      .getServerObjectInstanceById(serverObjectInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataAndShowDialog(): Promise<void> {
    //Create a List of the available Server Objects that the Instance can be a type of
    this.mAvailableEntityTypes = await this.mServerObjectService.getAllServerObjects();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.serverObject == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.serverObject.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.properties = this.mInstance.serverObject.properties;
    }

    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Show the Entity Instance Dialog
    this.mEntityInstanceDialogComponent.showDialog();
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Server Object Instance to be created
    this.mInstance.serverObject = this.mInstanceEntityType;

    if (this.mInstance.id == null) {
      //ID is null, Server Object Instance does not exist!  Create it...
      this
        .mServerObjectService
        .createServerObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshServerObjectInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Server Object Instance exists!  Update it...
      this
        .mServerObjectService
        .updateServerObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshServerObjectInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: ServerObject): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mServerObjectService
        .getServerObjectById(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  refreshInstanceData(): void {
    this
      .mServerObjectService
      .getServerObjectInstanceById(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }





  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {
      this
        .mServerObjectService
        .deleteServerObjects(this.getSelectedServerObjectIds())
        .then(
          response => {
            this.refreshServerObjects();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) {
      this
        .mServerObjectService
        .deleteServerObjectInstances(this.getSelectedServerObjectInstanceIds())
        .then(
          response => {
            this.refreshServerObjectInstance();
          },
          error => this.handleError(error)
        );
    }
  }


    /******************** FILTER FUNCTIONS FOR Server OBJECTS *****************/

    private refreshFilterOptionsForServerObjects(): void {
      this.filterOptionForServerObjects = [
        REMOVE_FILTER,
        new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
      ];
    }

    // Called by <app-filter-bar> whenever the active filter or its value changes.
    onServerObjectFilterChanged(option: FilterOption): void {
      this.selectedFilterOptionForServerObject = option;
      this.currentPageForServerObject = 1;
      this.refreshServerObjects();
    }
  
    filterStringChangedServerObjects(pageNumber?: number): void {
      if (!pageNumber) { pageNumber = 1; }
      // Show only size on this new page
      console.log("page Changed to " + pageNumber);
      this.currentPageForServerObject = pageNumber;
  
      this.pageChangedServerObject(pageNumber);
  
    }
  
  
      /******************** FILTER FUNCTIONS FOR Server OBJECTS INSTANCES *****************/
  
    private refreshFilterOptionsForServerObjectInstances(): void {
      this.filterOptionForServerObjectInstances = [
        REMOVE_FILTER,
        new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
        new FilterOption({ column: "serverObject.name", label: "Type", type: FilterType.STRING }),
      ];
    }

    // Called by <app-filter-bar> whenever the active filter or its value changes.
    onServerObjectInstanceFilterChanged(option: FilterOption): void {
      this.selectedFilterOptionForServerObjectInstance = option;
      this.currentPageForServerObjectInstance = 1;
      this.refreshServerObjectInstance();
    }
  
    filterStringChangedServerObjectInstances(pageNumber?: number): void {
      if (!pageNumber) { pageNumber = 1; }
      // Show only size on this new page
      console.log("page Changed to " + pageNumber);
      this.currentPageForServerObjectInstance = pageNumber;
  
      this.pageChangedServerObjectInstance(pageNumber);
  
    }
  
    handleError(error: any): void {
      /* New handling (TJB 04/18/2023) */
      if (error?.error != null) {
        //alert(error.error);
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);

      } 
      /* Default back to old handling */
      else if (error != null) {
        const response: Response = error;
        alert(response.text());
      }
    }
  
  
    /******************** PAGINATOR FUNCTIONS FOR Server OBJECT  *****************/
  
    pageChangedServerObject(pageNumber: number): void {
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }
  
      if(this.currentPageForServerObject == pageNumber) {
        return;
      }
  
      this.currentPageForServerObject = pageNumber;
      this.refreshServerObjects();
    }
  
  
    private async refreshServerObjects(): Promise<void> {
      this.spinnerService.show();
      let pagedData: PagedResults<ServerObject>;
      try {
        pagedData = await this.mServerObjectService.getAllServerObjectsForPaging((this.currentPageForServerObject - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.selectedFilterOptionForServerObject.apiValue);
      } catch (error) {
        this.spinnerService.hide();
        this.handleError(error);
        return;
      }
  
      this.updatePagedServerObject(pagedData);
      this.spinnerService.hide();
    }
  
  
    private updatePagedServerObject(pagedData: PagedResults<ServerObject>): void {
      const uiObjects: ServerObjectUI[] = [];
      if(pagedData.data != null) {
        pagedData.data.forEach(ServerObject => {
          const ui = new ServerObjectUI();
          ui.mServerObject = ServerObject;
          ui.mSelected = false;
          uiObjects.push(ui);
        })
      }
      this.mServerObjects = uiObjects;
  
      this.totalNumberOfServerObjects = pagedData.size;
    }
  
  
    /******************** PAGINATOR FUNCTIONS FOR Server OBJECT INSTANCES *****************/
  
    pageChangedServerObjectInstance(pageNumber: number): void {
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }
  
      if(this.currentPageForServerObjectInstance == pageNumber) {
        return;
      }
  
      this.currentPageForServerObjectInstance = pageNumber;
      this.refreshServerObjectInstance();
    }
  
    private async refreshServerObjectInstance(): Promise<void> {
      this.spinnerService.show();
      let pagedData: PagedResults<ServerObjectInstance>;
      try {
        pagedData = await this.mServerObjectService.getAllServerObjectInstancesForPagingWithFilters((this.currentPageForServerObjectInstance - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.activeFilterOptionsForServerObjectInstance);
      } catch (error) {
        this.spinnerService.hide();
        this.handleError(error);
        return;
      }
      
      this.updatePagedServerObjectInstance(pagedData);
      this.spinnerService.hide();
    }
  
    private updatePagedServerObjectInstance(pagedData: PagedResults<ServerObjectInstance>): void {
      const uiObjects: ServerObjectInstanceUI[] = [];
      if(pagedData.data != null) {
        pagedData.data.forEach(ServerObjectInstances => {
          const ui = new ServerObjectInstanceUI();
          ui.mServerObjectInstance = ServerObjectInstances;
          ui.mSelected = false;
          uiObjects.push(ui);
        })
      }
  
      this.mServerObjectInstances = uiObjects;
      this.totalNumberOfServerObjectInstances = pagedData.size;
    }
  
     /******************** RESULTS PER PAGE FUNCTIONS  *****************/
  
    manageResultPerPageClickedForTypes(value: number): void{
      this.selectedPageSizeValueForTypes = value;
      this.currentPageForServerObject = 1;
      this.refreshServerObjects();
    }
  
    manageResultPerPageClickedForInstances(value: number): void {
      this.selectedPageSizeValueForInstances = value;
      this.currentPageForServerObjectInstance = 1;
      this.refreshServerObjectInstance();
    }
  
  /*  
*  Handling the Right click event only on Server-Objects Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, serverObjectInstance: ServerObjectInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Server-Objects Instance details using the ID
    this
      .mServerObjectService
      .getServerObjectInstanceById(serverObjectInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.serverObject) && (response.serverObject).length > 0) {
              this.functionList = response.serverObject

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }

}

class ServerObjectUI {
  mSelected = false;
  mServerObject: ServerObject;
}

class ServerObjectInstanceUI {
  mSelected = false;
  mServerObjectInstance: ServerObjectInstance;
}